import { JsonServiceClient } from '@servicestack/client';
import {
    GetPatientInfo,
    GetPatientInfoResponse,
    ConfirmedArrivedRequest,
    ConfirmedArrivedResponse,
    PatientIndividualInformationDto,
    UpdatePatientInfoResponse,
    StartApplicationRequest,
    StartApplicationResponse,
    AnswerToSatisfactionSurveyResponse,
    AnswerToSatisfactionSurveyRequest,
    LogInteractionEventRequest,
    InteractionType,
    ErrorCodes,
    GetClinicInformationResponse,
    GetClinicInformationRequest,
    ClientEventScreen,
    GetAppointmentsResponse,
    GetAppointmentsRequests,
    UpdatePatientInfo,
    LogFrontEndExceptionRequest,
    SearchPharmaciesRequest,
    SearchPharmaciesResponse,
} from '../dtos/KioskDtos.dtos';
import { ParsedAddress } from '../dtos/ParsedAddress'
import axios,
{
    AxiosPromise,
    CancelTokenSource
} from 'axios';
import AddressParser from './AddressParser';
import { Authenticate, AuthenticateResponse } from '../dtos/KioskDtos.dtos';
import { StartApplicationPayload } from '../sagas/ApplicationAsyncSagas/types';
import moment = require('moment');
import {
    PostSurveyResultToServerAction
} from '../sagas/RequestSagas/types';
import { CountryCodes } from '../constants/countries';

declare let global;
const client = new JsonServiceClient(global.BaseUrl);

const CANADA_POST_API_KEY = process.env.CANADA_POST_API_KEY;
const GET_ADDRESS_LIST_URL_1 = `https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3ex.ws?Key=${CANADA_POST_API_KEY}&Country=`;
const GET_ADDRESS_LIST_URL_2 = `https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3ex.ws?Key=${CANADA_POST_API_KEY}&LanguagePreference=`;

export default class Client {
    public static getClinicInformation(): Promise<GetClinicInformationResponse> {
        return client.get(new GetClinicInformationRequest());
    }

    /**
     * Simple 
     * @param userName 
     * @param password 
     */
    public static postLoginInformation(
        userName: string,
        password: string,
        authenticationCode: string | null
    ): Promise<AuthenticateResponse> {
        const request = new Authenticate({
            userName,
            password: JSON.stringify({ password, authenticationCode })
        });
        return client.post(request);
    }

    /**
     * Send a request to search for all patients with specific health card number.
     * @param healthCardNumber
     * @param onSuccess 
     * @param onFail 
     */
    public static getPatientInfo(request: GetPatientInfo): Promise<GetPatientInfoResponse> {
        return client.get(request)
    }

    /**
     * Update the info that the personal information the user changed.
     * @param patientInfo 
     * @param onSuccess
     * @param onFail 
     */
    public static putPatientInfo(patientInfo: PatientIndividualInformationDto): Promise<UpdatePatientInfoResponse> {
        patientInfo.healthCard.expiryDate = moment(patientInfo.healthCard.expiryDate).toISOString();
        patientInfo.demographics.dateOfBirth = moment(patientInfo.demographics.dateOfBirth).toISOString();
        const request = new UpdatePatientInfo({ patientInfo });
        return client.put(request);
    }

    /**
     * Get the list of appointments that correspond to the patientid. Server side will only return today's appointments
     * @param patientId
     * @param onSuccess 
     * @param onFail 
     */
    public static getAppointments(patientId: string): Promise<GetAppointmentsResponse> {
        const request = new GetAppointmentsRequests();
        request.patientId = patientId;
        return client.get(request);
    }

    /**
     * Get the list of pharmacies that correspond to the search string from Medfar Dictionary.
     * @param searchString
     * @param pageSize
     * @param isContactDirectoryEnabled
     * @param enableExtendedGeographicalSearch
     * @param onSuccess 
     * @param onFail 
     */
    public static getPharmacyListFromMedfarDictionary(searchString: string, pageSize: number, isContactDirectoryEnabled: boolean, enableExtendedGeographicalSearch: boolean): Promise<SearchPharmaciesResponse> {
        const request = new SearchPharmaciesRequest();
        request.SearchString = searchString;
        request.PageSize = pageSize;
        request.IsContactDirectoryEnabled = isContactDirectoryEnabled;
        request.EnableExtendedGeographicalSearch = enableExtendedGeographicalSearch;
        return client.get(request);
    }

    /**
     * Send an event to the server to change the appointment status.
     * @param appointmentIds 
     * @param onSuccess 
     * @param onFail 
     */
    public static postArrivalStatus(appointmentIds: string[]): Promise<ConfirmedArrivedResponse> {
        const request = new ConfirmedArrivedRequest({ appointmentIds });
        return client.post(request);
    }

    public static postSurveyResult(action: PostSurveyResultToServerAction): Promise<AnswerToSatisfactionSurveyResponse> {
        return client.post(new AnswerToSatisfactionSurveyRequest({ ...action.payload }));
    }

    public static getPostCanadaAddressList(searchString: string, country: CountryCodes, lang: string, onSuccess?: (value: any) => void, onFail?: (value: ErrorCodes | string) => void): AxiosPromise<any> {
        let call: CancelTokenSource;
        if (call) {
            call.cancel("")
        }
        call = axios.CancelToken.source();
        let searchCountry = "CAN";
        if (country === CountryCodes.US) {
            searchCountry = "USA";
        } else if (country === CountryCodes.CA) {
            searchCountry = "CAN";
        } else {
            searchCountry = null;
        }
        if (searchCountry) {
            const fullUrl = `${GET_ADDRESS_LIST_URL_1}${searchCountry}&SearchTerm=${searchString}&SearchFor=Everything&LanguagePreference=${lang}&LastId=&SearchFor=Everything&OrderBy=UserLocation&$block=true&$cache=true&MaxSuggestions=20&MaxResults=100`;
            return axios.get(fullUrl, { cancelToken: call.token })
        }
        return null;
    }

    public static startApplication(applicationPayload: StartApplicationPayload): Promise<StartApplicationResponse> {
        return client.post(new StartApplicationRequest({ ...applicationPayload }));
    };

    public static getPostCanadaAddressList2(id: string, country: CountryCodes, lang: string): AxiosPromise<any> {
        let call: CancelTokenSource;
        if (call) {
            call.cancel("")
        }
        call = axios.CancelToken.source();
        let searchCountry = "CAN";
        if (country === CountryCodes.US) {
            searchCountry = "USA";
        } else if (country === CountryCodes.CA) {
            searchCountry = "CAN";
        } else {
            searchCountry = null;
        }
        if (searchCountry) {
            return axios.get(GET_ADDRESS_LIST_URL_2 + lang + "&LastId=" + id + "&MaxSuggestions=7&MaxResults=100",
                { cancelToken: call.token })
        }
        return null;
    }

    public static parsedResponse(response: any, countryCode: CountryCodes) {
        const searchTextArray = [];
        const addressObjectArray: ParsedAddress[] = [];
        for (const itm of response.data.Items) {
            let parsedData = {
                streetAddress: "",
                country: "",
                state: "",
                city: "",
                zipCode: "",
                moreAddresses: false,
                id: ""
            };
            if (countryCode === CountryCodes.CA) {
                parsedData = AddressParser.parseCanadianAddress(itm.Description, itm.Text);
            }
            else if (countryCode === CountryCodes.US) {
                parsedData = AddressParser.parseUsaAddress(itm.Description, itm.Text);
            }
            if (parsedData !== null) {
                parsedData.moreAddresses = (itm.Next === "Find");
                parsedData.id = itm.Id;
                searchTextArray.push(itm.Text + ", " + itm.Description);
                addressObjectArray.push(parsedData);
                parsedData = null;
            }
        }
        return { searchTextArray, addressObjectArray };
    }

    public static logInteractionEvent(interactionType: InteractionType, clientEventScreen?: ClientEventScreen) {
        client.post(new LogInteractionEventRequest({ interactionType, clientEventScreen }));
    };

    public static logFrontEndApiException(error: any) {
        client.post(new LogFrontEndExceptionRequest({ error }));
    };

    public static logManualCancelEvent(clientEventScreen: ClientEventScreen) {
        client.post(new LogInteractionEventRequest({ interactionType: InteractionType.PatientCancelledCheckinManually, clientEventScreen }));
    }

    public static logPatientDidNotAnswerSurveyEvent() {
        client.post(new LogInteractionEventRequest({ interactionType: InteractionType.DidNotAnswerSatisfactorySurvey }));
    }
}



// WEBPACK FOOTER //
// ./src/services/Client.ts